import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { makeFormField, chooseRandom, slugify } from '@/helpers';
import { Routes } from '@/router';
import { Menu, MenuItemGroup, MenuItemGroupType, MenuMetadataForChannel, MenuSidesAndDrinks, OrderMealRequest, OrderType, PreConfiguredMeal, Restaurant, TortillaItem } from '@/models';
import { some } from 'lodash';
import { getTopLevelMenuInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';

@Component({
  name: 'CmgAddMissingItemForRewardModal',
})
export default class CmgAddMissingItemForRewardModal extends Vue {
  menuItem: MenuItemGroup | undefined;
  position: number | undefined;
  name = makeFormField();
  saving = false;
  noItemSelected = false;
  isSide = false;
  isDrink = false;
  isAddNewEntree = false;
  filteredMenu: any[] = [];
  componentKey = 0;

  get args() {
    return this.$appStore.state.ui.modals.addMissingItemForReward.args;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.addMissingItemForReward.visible;
  }

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }
  get groupCopy(): Menu {
    return JSON.parse( JSON.stringify( this.menu ) );
  }
  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isMenuLoading(): boolean {
    return this.$appStore.state.menu.isLoadingRestaurantMenu;
  }

  get menuMetadata(): MenuMetadataForChannel | null {
    return this.$appStore.state.menu.metadata;
  }

  get preconfiguredMeals(): PreConfiguredMeal[] {
    return this.$appStore.state.menu.preConfiguredMeals;
  }

  get arrowRightImageUrl(): string {
    return this.$appStore.state.cms.images.icons.arrowRightGold.default;
  }

  get isAnEntree(): boolean | undefined {
    return this.args?.rewardText.toLowerCase().includes('entree')
  }

  get isASingleSelect(): boolean | undefined {
    return this.includesAnyIgnoreCase(this.args?.rewardText.toLowerCase() ?? '', ['chips &', 'drink', 'queso', 'guac', 'beverage'])
  }

  get isSingleSelectOptionalMultiSelect(): boolean | undefined {
    return this.args?.rewardText.toLowerCase().includes('queso') || this.args?.rewardText.toLowerCase().includes('guac')
  }

  get modalButtonText(): string {
    return this.isAnEntree? 'continue' : 'add to bag'
  }

  get selectedIndex(): number | undefined {
    return this.position;
  }

  //@todo figure out how to pull instant selects and forego modal, this may happen on bag for this one
  // get isAnInstantSelect(): boolean | undefined {
  //   return this.args?.rewardText.toLowerCase().includes('chips &')
  // }

  get rewardText(): string | undefined {
    return this.args?.rewardText
  }

  includesAnyIgnoreCase(str: string, options: string[]): boolean {
    const lowerStr = str.toLowerCase();
    return options.some(opt => lowerStr.includes(opt.toLowerCase()));
  }

  reloadComponent() {
    this.componentKey++ // Changing key triggers re-render
  }

  //logic to pass this category to the modal from bag add item and render appropriate filtered items accordingly
  //@TODO: replace this for FAD-625 this.filteredMenu should filter based off the list of provided CMGs from the promo call
  getFilteredMenuForRewards(): any[] {
    if (!this.menu || !this.menuMetadata) {
      return [];
    }
    //setting this to true will cause infinite loop if we are on home page or any page where top level menu is displayed
    const allMenuGroups = this.$services.metadata.getValidMenuGroupsForMenu(this.menu, this.menuMetadata, false);
    if(this.isAddNewEntree) {
      const allowedEntrees = ['Burrito', 'Burrito Bowl', 'Salad', 'Tacos', 'Quesadilla'];
      this.filteredMenu = allMenuGroups.filter(g => { return allowedEntrees.includes(g.displayName)})
    //update this to be dynamic for sides, drinks, etc
    }  
    if(this.isAnEntree) {
      const allowedEntrees = ['Burrito', 'Burrito Bowl', 'Salad', 'Tacos'];
      this.filteredMenu = allMenuGroups.filter(g => { return allowedEntrees.includes(g.displayName)})
    //update this to be dynamic for sides, drinks, etc
    }   
    if(this.isASingleSelect && !this.isAddNewEntree) {
      let filteredSides: MenuSidesAndDrinks[] | undefined;
      if(this.args?.rewardText.toLowerCase().includes('chips &')){
        filteredSides = this.groupCopy.sides?.filter((s: { itemType: string; }) => s.itemType === "ChipsSalsa");
        this.isSide = true;
      }
      //bottled drinks
      if(this.args?.rewardText.toLowerCase().includes('bottled')){
        //since tractor and tractor rtd both have the same first word we want to retain the bottles so they don't get filtered out here
        const bottledTractor = this.groupCopy.drinks?.filter((s: { itemType: string; }) => s.itemType === "Tractor RTD");
        filteredSides = this.groupCopy.drinks?.filter((s: { itemType: string; }) => !this.includesAnyIgnoreCase(s.itemType, ['tractor', 'soda']));
        if(bottledTractor && bottledTractor.length > 0) {
          filteredSides = filteredSides?.concat(bottledTractor);
        }
        this.isDrink = true;
      }
      //fountain drinks
      if(this.args?.rewardText.toLowerCase().includes('fountain')){
        filteredSides = this.groupCopy.drinks?.filter((s: { itemType: string; itemName: string; }) => this.includesAnyIgnoreCase(s.itemType, ['Soda', 'Tractor']) && !this.includesAnyIgnoreCase(s.itemType, ['Tractor RTD']) && !s.itemName.includes('32 fl'));
        filteredSides = filteredSides?.map((side: any) => ({
          ...side,
          itemName: side.itemName.replace('22 fl oz', '').trim()
        }));
        this.isDrink = true;
      }
      //special case only for new accounts, not in regular rewards, display both fountain and bottled drinks?
      if(this.args?.rewardText.toLowerCase().includes('beverage') || this.args?.rewardText.toLowerCase().includes('free drink')){
        filteredSides = this.groupCopy.drinks?.filter((s: { itemName: string; }) => !s.itemName.includes('32 fl'));
        filteredSides = filteredSides?.map((side: any) => ({
          ...side,
          itemName: side.itemName.replace('22 fl oz', '').trim()
        }));
        this.isDrink = true;
      }
      //items that have single select and add to new entree options
      let addNewEntreeStub = {} as any;
      if(this.isSingleSelectOptionalMultiSelect){
        if(this.args?.rewardText.toLowerCase().includes('queso')){
          filteredSides = this.groupCopy.sides?.filter((s: { itemName: string; }) => s.itemName === "Side of Queso Blanco");
        }
        if(this.args?.rewardText.toLowerCase().includes('guac')){
          filteredSides = this.groupCopy.sides?.filter((s: { itemName: string; }) => s.itemName === "Side of Guacamole");
        }
        
        this.isSide = true;
        addNewEntreeStub = {
          "displayName": 'Add New Entree',
          "itemCategory": "Entree",
          "thumbnailImageUrl": "https://www-qa.chipotle.com/content/dam/chipotle/menu/meal-types/burrito/web-desktop/burrito.png",
        };
      }
      
      if(filteredSides && filteredSides.length > 0){
        const itemMetadata = this.menuMetadata?.items;
        filteredSides?.forEach((side: any) => {
          side.displayName = side.itemName;
          // Set isSelected based on current selected
          // side.isSelected = false;
          if(itemMetadata) {
            side.thumbnailImageUrl = itemMetadata?.[side.itemId]?.thumbnailUrl;
          }
        })
        if(Object.keys(addNewEntreeStub).length > 0){
          filteredSides = filteredSides?.concat([addNewEntreeStub]);
        }
      }

      this.filteredMenu = filteredSides ?? [];
    }
    return this.filteredMenu;
  }

  getGroupThumbnailUrl(group: MenuItemGroup) {
    const targetImageConent =  this.$appStore.state.adobeTarget.topLevelMenuImages.find(t => t.topLevelMenuTitle == group.displayName);

    return targetImageConent ? targetImageConent.thumbnailImageUrl : group.thumbnailImageUrl;
  }

  onItemGroupClick(group: MenuItemGroup, itemPosition: number) {
    //add flow here to change to modal containing all selectable entrees including quesadilla
    if(group.displayName.toLowerCase().includes('add new')){
      this.isAddNewEntree = true;
      this.getFilteredMenuForRewards();
    }
    if(group) {
      group.isSelected = true;
      this.menuItem = group;
    }
    if(itemPosition >= 0) {
      this.position = itemPosition;
    }
    this.reloadComponent();
  }

  //@TODO add logic for single select
  continueOrAddToBag() {
    if (!this.menuItem || !this.isItemGroupAvailable(this.menuItem)) {
      this.noItemSelected = true;
      return;
    }
    this.noItemSelected = false;
    if(this.menuItem && this.position != undefined && this.position >= 0){
      if(this.isAnEntree || this.isAddNewEntree){
        this.$services.googleAnalytics.sendProductClickEvent(this.menuItem.displayName);
        this.$services.analytics.addLinkClickedEventToStore(getTopLevelMenuInteraction(this.position, this.menuItem.displayName.toUpperCase()));

        // const routeName = this.orderType == OrderType.Regular ? Routes.MealBuilder : Routes.GroupOrderMealBuilder;
        this.$router.push({ name: Routes.MealBuilder, params: { groupName: slugify(this.menuItem.displayName) } });
        this.$services.ui.setModalVisibility('addMissingItemForReward', false);
        this.$services.bag.setBagVisibility(false);
        this.isAddNewEntree = false;
        return;
      }
      //single select for now, will have to add a 3rd option for instant add
      if(!this.isAnEntree){
        console.log('not an entree add selected item to bag from this modal', this.menuItem)
        const order = this.$services.order.getCurrentOrder()
        if(!order){
          return;
        }
        //@todo swap to this and update existing order instead of putting sides seperately
        if(order.order?.meals && order.order.meals.length > 0) {
          const mealId =  order.order.meals[0].mealId;
          const menuItemFormatted = {
            menuItemId: this.menuItem.itemId ?? '',
            menuItemName: this.menuItem.itemName ?? '',
            quantity: 1,
            isUpSell: false,
            customizationId: undefined,
          };
          const firstMeal = order.order.meals[0];
          const mealRequest: OrderMealRequest = {
            meal: {
              mealName: firstMeal?.mealName ?? 'temp',
              entrees: firstMeal?.entrees && firstMeal.entrees.length > 0 ? firstMeal.entrees.map(e => ({...e, contents: e.contents ?? []})) : null,
              sides: firstMeal?.sides && firstMeal.sides.length > 0 ? firstMeal.sides : this.isSide ? [menuItemFormatted]: null,
              drinks: firstMeal?.drinks && firstMeal.drinks.length > 0 ? firstMeal.drinks : this.isDrink? [menuItemFormatted] : null,
            },
          };
          this.$services.order.updateMealInOrder(order, mealId, mealRequest);
          this.$services.ui.setModalVisibility('addMissingItemForReward', false);
        }
        
        //@todo swap to update instead of putting sides seperately, this portion will get removed
        // const menuItemFormatted = {
        //   menuItemId: this.menuItem.itemId ?? '',
        //   menuItemName: this.menuItem.itemName ?? '',
        //   quantity: 1,
        //   isUpSell: false,
        //   customizationId: undefined,
        // };
        // // const createMealRequest = this.$services.mealBuilder.buildMealRequest(MultiselectSingleEntreeTypeBuilderMode, this.menuItem, 'bob');
        // const mealRequest: OrderMealRequest = {
        //   meal: {
        //     mealName: 'bob',
        //     entrees: null,
        //     sides: this.isSide ? [menuItemFormatted] : null,
        //     drinks: this.isDrink ? [menuItemFormatted] : null,
        //   },
        // };
        // this.$services.order.addMealToOrder(order, mealRequest);
        // this.$services.ui.setModalVisibility('addMissingItemForReward', false);
      }
    }
  }

  // For default items, we can use the item lookup map
  isDefaultTopLevelMenuItemAvailable(group: MenuItemGroup): boolean {
    const menuItems = this.$appStore.state.menu.menuItemLookupMap;

    if (!menuItems) {
      return false;
    }

    return some(group.items, item => {
      const availableItem = menuItems.get(item.menuItemId);
      return availableItem && availableItem.isItemAvailable;
    });
  }

  
  isItemGroupAvailable(group: MenuItemGroup): boolean {
    // If restaurant isn't selected or menu is loading, we need to make the menu
    // items appear to be available even if they may not. Otherwise the items
    // will appear unavailable temporarily
    if (this.isMenuLoading || !this.selectedRestaurant) {
      return true;
    }

    if (!group) {
      return false;
    }

    switch (group.type) {
      case MenuItemGroupType.Default:
        return this.isDefaultTopLevelMenuItemAvailable(group);
      case MenuItemGroupType.PreConfigured:
        return this.preconfiguredMeals && some(this.preconfiguredMeals, pce => pce.mealType == group.menuItemType);
      case MenuItemGroupType.Nested:
        // Check if nested Kids BYO or Kids Quesadilla is available
        return this.$services.menu.areTortillaItemsAvailable(TortillaItem.KidsByo) || this.$services.menu.areTortillaItemsAvailable(TortillaItem.KidsQuesadilla);
      case MenuItemGroupType.Quantity:
        return this.$services.menu.areTortillaItemsAvailable(TortillaItem.Tacos);
      default:
        return true;
    }
  }

  close() {
    this.$services.ui.setModalVisibility('addMissingItemForReward', false);

    Vue.nextTick(() => {
      setTimeout(() => {
        this.$services.ui.focusBack();
      }, 1000);
    });
  }
}
